import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { VersionPublishForm } from './version-publish-form';
import { VersionContentPane } from './version-content-pane';

/**
 * The versions section of a document.
 *
 * A version is immutable once published. Publishing a new version does not change an existing
 * one, and the requirement form binds to a specific version.
 *
 * The content pane answers "what does the selected version say?". It reads nothing until a
 * version is selected — including the one the page selects when a publish succeeds.
 */
export function VersionsSection(props: {
  documentId: string;
  selectedVersionId: string | null;
  onChanged: () => void;
  onSelectVersion: (versionId: string) => void;
}): ReactNode {
  const [showForm, setShowForm] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-content-muted">
          A version is immutable once published. Publishing a new version does not change an
          existing one.
        </p>
        <Button size="sm" onClick={() => setShowForm((value) => !value)}>
          {showForm ? 'Close' : 'Publish version'}
        </Button>
      </div>
      {showForm ? (
        <VersionPublishForm
          documentId={props.documentId}
          onPublished={(versionId) => {
            setShowForm(false);
            props.onSelectVersion(versionId);
            props.onChanged();
          }}
        />
      ) : null}
      <VersionContentPane
        documentId={props.documentId}
        selectedVersionId={props.selectedVersionId}
      />
      <p className="text-sm text-content-muted">
        Select a version in the Requirements tab to bind a requirement to it.
      </p>
    </div>
  );
}
